import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import type { Alias, AliasKind, FimSubjectRecord } from 'features/fim/types'

import { sha256Hex } from './sha256'
import type { PhotoElection, PhotoNaturalization, PhotoStatus } from './types'

/** The subject-side identity an election pins to, plus how it will land. */
export interface ElectionTarget {
  norm: string
  keyless: boolean
  naturalizesAt: PhotoNaturalization
}

const UID_PART_SAFE = /^[A-Za-z0-9._-]+$/
const UID_PART_UNSAFE_RUN = /[^A-Za-z0-9._-]+/g
const UID_MAX = 63

/** fim-service `stable_hash`: sha256 of the parts joined by `::`, first 32 hex. */
const stableHash = (...parts: string[]): string =>
  sha256Hex(parts.join('::')).slice(0, 32)

const stripEnd = (text: string, chars: string): string => {
  let end = text.length
  while (end > 0 && chars.includes(text[end - 1])) end -= 1
  return text.slice(0, end)
}

const stripStart = (text: string, chars: string): string => {
  let start = 0
  while (start < text.length && chars.includes(text[start])) start += 1
  return text.slice(start)
}

/** fim-service `_uid_part`: id-safe text as is, else a cleaned form plus a
 * hash tail so two norms that clean identically cannot collide. */
function uidPart(raw: string): string {
  if (raw && UID_PART_SAFE.test(raw)) return raw
  const cleaned = stripStart(
    stripEnd(raw.replace(UID_PART_UNSAFE_RUN, '-'), '-'),
    '-'
  )
  const suffix = stableHash(raw).slice(0, 8)
  return cleaned ? `${cleaned}.${suffix}` : suffix
}

/** fim-service `_fit`: cap at Rolodex's id budget with a readable head and a
 * hash tail. Lengths count code points, as Python's `len` does. */
function fit(uid: string): string {
  const codePoints = Array.from(uid)
  if (codePoints.length <= UID_MAX) return uid
  const head = stripEnd(codePoints.slice(0, UID_MAX - 9).join(''), '-.$')
  return `${head}.${stableHash(uid).slice(0, 8)}`
}

/** fim-service `tag_uid`: the uid of the subject that OWNS a canonical tag. */
export const tagUid = (siteId: string, norm: string): string =>
  fit(`${uidPart(siteId)}$${uidPart(norm)}`)

const photoDerived = (alias: Alias): boolean =>
  alias.references.length > 0 &&
  alias.references.every((reference) => reference.type === 'photo')

const isIdentityTag = (alias: Alias): boolean =>
  alias.kind === 'canonical_tag' && alias.precision === 'high' && !!alias.norm

const TEXT_KIND_RANK: Partial<Record<AliasKind, number>> = {
  description: 0,
  contextual_name: 1,
  ocr_variant: 2,
}

const textKindRank = (kind: AliasKind): number =>
  TEXT_KIND_RANK[kind] ?? Object.keys(TEXT_KIND_RANK).length

const compareText = (a: Alias, b: Alias): number => {
  const byPrecision =
    (a.precision === 'high' ? 0 : 1) - (b.precision === 'high' ? 0 : 1)
  if (byPrecision !== 0) return byPrecision
  const byKind = textKindRank(a.kind) - textKindRank(b.kind)
  if (byKind !== 0) return byKind
  if (a.norm === b.norm) return 0
  return a.norm < b.norm ? -1 : 1
}

const isTextCandidate = (alias: Alias): boolean =>
  !!alias.norm &&
  alias.kind !== 'canonical_tag' &&
  alias.kind !== 'functional_location' &&
  !photoDerived(alias)

/** fim-service `_norm_is_bindable`: fusion's must-link binds only by a
 * high-precision alias that is not a functional location. */
const bindable = (aliases: Alias[], norm: string): boolean =>
  aliases.some(
    (alias) =>
      alias.norm === norm &&
      alias.precision === 'high' &&
      alias.kind !== 'functional_location'
  )

/**
 * The norm a photo election pins to, the rule shared with fim-service's
 * `subject_identity_norm`: the canonical tag the subject OWNS (the tag its
 * uid is minted from), else the best text alias (high precision first, then
 * description, contextual name, OCR variant, then by norm), else null.
 * Unowned tags and functional locations are never pinned: fusion applies
 * the election as a forced merge, so pinning a tag another subject owns
 * would merge that subject in. Photo-derived aliases never qualify, so one
 * election cannot chain on another's anchor.
 */
export function electionTarget(
  siteId: string,
  subject: FimSubjectRecord
): ElectionTarget | null {
  const aliases = subject.data.aliases
  const tags = aliases.filter(isIdentityTag)
  const owned = tags.find(
    (alias) => tagUid(siteId, alias.norm) === subject.subjectUid
  )
  const best = owned ?? aliases.filter(isTextCandidate).sort(compareText).at(0)
  if (!best) return null
  return {
    norm: best.norm,
    keyless: tags.length === 0,
    naturalizesAt: bindable(aliases, best.norm)
      ? 'next_fusion_run'
      : 'after_subject_gains_identity',
  }
}

/** Tooltip and lightbox note while a photo waits on the next model sync. */
export const PHOTO_PENDING_HINT: MessageDescriptor = msg`pending model sync`

/** The elections with one added for `target`; unchanged when it already exists. */
export function withElection(
  elections: PhotoElection[],
  target: ElectionTarget,
  subjectUid: string,
  equipmentKind: string,
  electedAt: string
): PhotoElection[] {
  if (elections.some((e) => e.subject_identity_norm === target.norm)) {
    return elections
  }
  const n = elections.reduce((max, e) => Math.max(max, e.n), -1) + 1
  return [
    ...elections,
    {
      n,
      subject_identity_norm: target.norm,
      subject_uid_at_election: subjectUid,
      equipment_kind: equipmentKind,
      elected_at: electedAt,
    },
  ]
}

/**
 * The elections without the one pinning this subject. Norm-first like
 * fim-service; the recorded uid is only the fallback for a subject that no
 * longer resolves to any norm.
 */
export function withoutElection(
  elections: PhotoElection[],
  target: ElectionTarget | null,
  subjectUid: string
): PhotoElection[] {
  return target
    ? elections.filter((e) => e.subject_identity_norm !== target.norm)
    : elections.filter((e) => e.subject_uid_at_election !== subjectUid)
}

export function withHero(
  elections: PhotoElection[],
  target: ElectionTarget,
  heroAt: string
): PhotoElection[] {
  return elections.map((e) =>
    e.subject_identity_norm === target.norm ? { ...e, hero_at: heroAt } : e
  )
}

export const photoStatusFor = (elections: PhotoElection[]): PhotoStatus =>
  elections.length > 0 ? 'attached' : 'unattached'
